import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, TextField } from '@heroui/react';
import { FormDrawer } from '@/components/formDrawer';
import { ImageCropUpload } from '@/components/imageCropUpload';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { useSupplierForm } from './useSupplierForm';

type SupplierFormModalViewProps = {
    error: string | null;
    form: ReturnType<typeof useSupplierForm>;
    isOpen: boolean;
    isSubmitting: boolean;
    mode: 'create' | 'edit';
    onClose: () => void;
};

const SUPPLIER_IMAGE_CROP_SIZE = { width: 512, height: 512 };

export const SupplierFormModalView: React.FC<SupplierFormModalViewProps> = ({
    error,
    form,
    isOpen,
    isSubmitting,
    mode,
    onClose,
}) => {
    const { t } = useTranslation('pages');
    const {
        image,
        isValid,
        name,
        serialNumber,
        phone,
        email,
        showEmailError,
        onImageChange,
        onNameChange,
        onSerialNumberChange,
        onPhoneChange,
        onEmailChange,
        onEmailBlur,
        onSubmit,
    } = form;

    const copy = React.useMemo(
        () => ({
            title:
                mode === 'edit'
                    ? t('suppliers.components.supplierTable.form.editTitle')
                    : t('suppliers.components.supplierTable.form.title'),
            description: t(
                'suppliers.components.supplierTable.form.description',
            ),
            imageLabel: t('suppliers.components.supplierTable.form.imageLabel'),
            imageDescription: t(
                'suppliers.components.supplierTable.form.imageDescription',
            ),
            imageSelect: t(
                'suppliers.components.supplierTable.form.imageSelect',
            ),
            imageChange: t(
                'suppliers.components.supplierTable.form.imageChange',
            ),
            imageRemove: t(
                'suppliers.components.supplierTable.form.imageRemove',
            ),
            imageApply: t('suppliers.components.supplierTable.form.imageApply'),
            imageUploadLabel: t(
                'suppliers.components.supplierTable.form.imageUploadLabel',
            ),
            imageInvalidType: t(
                'suppliers.components.supplierTable.form.imageInvalidType',
            ),
            imageInvalidSize: t(
                'suppliers.components.supplierTable.form.imageInvalidSize',
            ),
            imageCropError: t(
                'suppliers.components.supplierTable.form.imageCropError',
            ),
            imageReadError: t(
                'suppliers.components.supplierTable.form.imageReadError',
            ),
            nameLabel: t('suppliers.components.supplierTable.form.nameLabel'),
            namePlaceholder: t(
                'suppliers.components.supplierTable.form.namePlaceholder',
            ),
            serialNumberLabel: t(
                'suppliers.components.supplierTable.form.serialNumberLabel',
            ),
            serialNumberPlaceholder: t(
                'suppliers.components.supplierTable.form.serialNumberPlaceholder',
            ),
            phoneLabel: t('suppliers.components.supplierTable.form.phoneLabel'),
            phonePlaceholder: t(
                'suppliers.components.supplierTable.form.phonePlaceholder',
            ),
            emailLabel: t('suppliers.components.supplierTable.form.emailLabel'),
            emailPlaceholder: t(
                'suppliers.components.supplierTable.form.emailPlaceholder',
            ),
            emailInvalid: t(
                'suppliers.components.supplierTable.form.emailInvalid',
            ),
            cancel: t('suppliers.components.supplierTable.form.cancel'),
            submit:
                mode === 'edit'
                    ? t('suppliers.components.supplierTable.form.editSubmit')
                    : t('suppliers.components.supplierTable.form.submit'),
        }),
        [mode, t],
    );

    return (
        <FormDrawer
            footer={
                <>
                    <Button
                        isDisabled={isSubmitting}
                        onPress={onClose}
                        variant="tertiary"
                        {...getNodeTestIdProp('supplier-form-cancel')}
                    >
                        {copy.cancel}
                    </Button>
                    <Button
                        isDisabled={!isValid}
                        isPending={isSubmitting}
                        onPress={onSubmit}
                        variant="primary"
                        {...getNodeTestIdProp('supplier-form-submit')}
                    >
                        {copy.submit}
                    </Button>
                </>
            }
            isOpen={isOpen}
            onClose={onClose}
            subtitle={copy.description}
            testId="supplier-form"
            title={copy.title}
        >
            <div
                className="flex flex-col gap-1.5"
                {...getNodeTestIdProp('supplier-form-image-field')}
            >
                <Label
                    className="text-sm font-medium text-foreground"
                    {...getNodeTestIdProp('supplier-form-image-label')}
                >
                    {copy.imageLabel}
                </Label>
                <ImageCropUpload
                    applyLabel={copy.imageApply}
                    aspectClassName="aspect-square"
                    boxClassName="mx-auto w-[150px]"
                    changeLabel={copy.imageChange}
                    cropErrorMessage={copy.imageCropError}
                    cropSize={SUPPLIER_IMAGE_CROP_SIZE}
                    description={copy.imageDescription}
                    id="supplier-form-image"
                    invalidSizeMessage={copy.imageInvalidSize}
                    invalidTypeMessage={copy.imageInvalidType}
                    label={copy.imageUploadLabel}
                    onChange={onImageChange}
                    readErrorMessage={copy.imageReadError}
                    removeLabel={copy.imageRemove}
                    selectLabel={copy.imageSelect}
                    value={image}
                />
            </div>
            <TextField
                isRequired
                onChange={onNameChange}
                value={name}
                {...getNodeTestIdProp('supplier-form-name')}
            >
                <Label>{copy.nameLabel}</Label>
                <Input placeholder={copy.namePlaceholder} />
            </TextField>
            <TextField
                onChange={onSerialNumberChange}
                value={serialNumber}
                {...getNodeTestIdProp('supplier-form-serial-number')}
            >
                <Label>{copy.serialNumberLabel}</Label>
                <Input placeholder={copy.serialNumberPlaceholder} />
            </TextField>
            <TextField
                onChange={onPhoneChange}
                value={phone}
                {...getNodeTestIdProp('supplier-form-phone')}
            >
                <Label>{copy.phoneLabel}</Label>
                <Input placeholder={copy.phonePlaceholder} />
            </TextField>
            <TextField
                onChange={onEmailChange}
                value={email}
                {...getNodeTestIdProp('supplier-form-email')}
            >
                <Label>{copy.emailLabel}</Label>
                <Input
                    onBlur={onEmailBlur}
                    placeholder={copy.emailPlaceholder}
                    type="email"
                />
            </TextField>
            {showEmailError ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('supplier-form-email-error')}
                >
                    {copy.emailInvalid}
                </p>
            ) : null}
            {error ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('supplier-form-error')}
                >
                    {error}
                </p>
            ) : null}
        </FormDrawer>
    );
};
